import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import styled from 'styled-components'

import { MODALS_ID, QUERY_KEY } from '../../constants'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/ModalProvider'

import { useUserInfo } from '../../hooks/useUserInfo'

import { getWithdrawalUser } from '../../api/my'

import { Head } from '../../components/head/Head'
import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { CenterWrap } from '../../components/common'
import { Button } from '../../components/button'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Footer, FOOTER_MIN_HEIGHT } from '../../components/footer'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { TitleContainer } from '../../components/titleContainer/TitleContainer'
import { WithdrawalConfirmModal } from './modals/WithdrawalConfirmModal'

const HEAD_INFO = {
  title: '회원 탈퇴 | 미술품 조각투자 플랫폼 아티피오',
  ogTitle: '회원 탈퇴 | 미술품 조각투자 플랫폼 아티피오',
  ogUrl: window.location.href,
}

export function WithdrawalUser() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)

  const { data: userInfo, isLogin, isCheckingLogin } = useUserInfo()

  const {
    data: withdrawalUserInfo,
    isPending,
    isError,
  } = useQuery({ queryKey: [QUERY_KEY.withdrawalUserInfo], queryFn: getWithdrawalUser })

  useEffect(() => {
    setShowTab(false)
  }, [])

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  if (!userInfo || isPending || isError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" title="회원탈퇴" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  const handleResignEvent = () =>
    openModal({ id: MODALS_ID.WITHDRAWAL_USER_CONFIRM, Component: WithdrawalConfirmModal })

  const isValidAsset = () => {
    const { depositBalance, totalShares, productCount, unclaimedBalance, unclaimedSharesCount, unclaimedProductCount } =
      withdrawalUserInfo

    /* 보유 예치금이 있을 경우 "탈퇴하기" 버튼 비활성화 */
    if (depositBalance !== 0) return true

    /* 보유 조각이 있을 경우 "탈퇴하기" 버튼 비활성화 */
    if (totalShares !== 0) return true

    /* 보유 작품이 있을 경우 "탈퇴하기" 버튼 비활성화 */
    if (productCount !== 0) return true

    /* 미수령 정산 대금이 있을 경우 "탈퇴하기" 버튼 비활성화 */
    if (unclaimedBalance !== 0) return true

    /* 미정산 보유 증권 수량이 있을 경우 "탈퇴하기" 버튼 비활성화 */
    if (unclaimedSharesCount !== 0) return true

    /* 미정산 보유 작품 수량이 있을 경우 "탈퇴하기" 버튼 비활성화 */
    if (unclaimedProductCount !== 0) return true

    return false
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="회원탈퇴" />
        <StyledContent>
          <TitleContainer
            title={`탈퇴하기 전에\n보유하신 자산을 확인해주세요.`}
            desc="보유자산 전부 처분 시 탈퇴가 가능해요."
          >
            <Text size={16} weight={700}>
              보유 자산
            </Text>
            <FlexBetween>
              <Text size={16} weight={500} color="text_secondary">
                보유 예치금
              </Text>
              <Text size={16} weight={700} color="error">
                {withdrawalUserInfo.depositBalance.toLocaleString()} 원
              </Text>
            </FlexBetween>
            <DivideLine />
            <FlexBetween>
              <Text size={16} weight={500} color="text_secondary">
                보유 증권 수량
              </Text>
              <Text size={16} weight={700} color="error">
                {withdrawalUserInfo.totalShares.toLocaleString()} 주
              </Text>
            </FlexBetween>
            <DivideLine />
            <FlexBetween>
              <Text size={16} weight={500} color="text_secondary">
                보유 작품 수량
              </Text>
              <Text size={16} weight={700} color="error">
                {withdrawalUserInfo.productCount.toLocaleString()} 개
              </Text>
            </FlexBetween>
            <DivideLine />
            <FlexBetween>
              <Text size={16} weight={500} color="text_secondary">
                미수령 정산 대금
              </Text>
              <Text size={16} weight={700} color="error">
                {withdrawalUserInfo.unclaimedBalance.toLocaleString()} 원
              </Text>
            </FlexBetween>
            <DivideLine />
            <FlexBetween>
              <Text size={16} weight={500} color="text_secondary">
                미정산 보유 증권 수량
              </Text>
              <Text size={16} weight={700} color="error">
                {withdrawalUserInfo.unclaimedSharesCount.toLocaleString()} 주
              </Text>
            </FlexBetween>
            <DivideLine />
            <FlexBetween>
              <Text size={16} weight={500} color="text_secondary">
                미정산 보유 작품 수량
              </Text>
              <Text size={16} weight={700} color="error">
                {withdrawalUserInfo.unclaimedProductCount.toLocaleString()} 개
              </Text>
            </FlexBetween>
            <DivideLine />
          </TitleContainer>
          <NoticeContainer
            title="안내사항"
            list={[
              '보유 예치금과 미수령 정산 대금이 있다면 탈퇴가 제한돼요. 미수령 정산 대금은 정산 처리를 해주시고, 보유한 예치금과 함께 모두 출금해 주세요.',
              '보유한 작품의 증권이 없어야 해요.',
              '자산 보유자가 탈퇴하더라도, 분할 소유권 투자자 명부에는 유지되며, 공증을 통해 보관돼요.',
              '탈퇴 시 보유한 작품의 권리 행사에 제한이 있을 수 있어요.',
              '탈퇴 후 투자자 명부에 기록된 개인정보는 보관되었다가 향후 매각 시 국세청에 세금 신고에 활용돼요.',
            ]}
            $backgroundColor="gray_1"
          />
          <Footer>
            <Button size="large" disabled={isValidAsset()} block={true} onClick={handleResignEvent}>
              탈퇴하기
            </Button>
          </Footer>
        </StyledContent>
      </Wrap>
    </>
  )
}

const StyledContent = styled(Content)`
  > *:first-child {
    flex-shrink: 1;
    height: 100%;
  }
`

const FlexBetween = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 24px 0 8px;
`
